import React, {type ReactNode, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Card, Empty, Skeleton, Table, Typography} from "antd";
import type {TableProps} from "antd";
import {handleScrollTo} from "@/components/table/responsiveHelper.ts";
import {applyColumnSkeletons, buildDisplayData, isSkeletonRow, wrapOnRowWithSelection, type ColumnsWithKey} from "./helpers.tsx";

type AutoHeightTableProps<T extends object> = Omit<TableProps<T>, "scroll"> & {
  /** Table column definitions */
  columns: ColumnsWithKey<T>;
  /** Data source for the table */
  data: T[];
  /** Whether the table is in loading state (shows skeleton rows) */
  loading?: boolean;
  /** Subtract this pixel value from available height (e.g., for header/toolbar above the table) */
  subtractPx?: number;
  /** Enable virtual scrolling for better performance with large datasets */
  enableVirtual?: boolean;
  /** Minimum body height in pixels */
  minBodyHeight?: number;
  /** Wrap the table in an Ant Design Card component */
  withCard?: boolean;
  /** Enable internal row selection handling. When enabled, manages selectedRowKey/selectedIndex internally and automatically handles row selection on click. */
  enableRowSelection?: boolean;
  /** Component before table  */
  beforeComponent?: ReactNode;
};

/**
 * Custom hook to automatically calculate available table body height.
 * Observes container size changes and subtracts header/title heights to determine
 * the exact space available for the table body content.
 */
function useAutoSize(ref: React.RefObject<HTMLElement>) {
  const [size, setSize] = useState({width: 0, height: 0});

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const tableTitle = el.querySelector(".ant-table-title");
    const tableHeader = el.querySelector(".ant-table-header");

    const tableHeaderHeight: number = tableHeader?.clientHeight ?? 0;
    const tableTitleHeight = tableTitle?.clientHeight ?? 0;
    const ro = new ResizeObserver(([entry]) => {
      const cr = entry.contentRect;
      // Calculate available height by subtracting header and title heights
      const crr = cr.height - tableHeaderHeight - tableTitleHeight;
      setSize({width: cr.width, height: crr});
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return size;
}

/**
 * AutoHeightTable - A feature-rich table component built on Ant Design Table.
 * 
 * Features:
 * - Automatic height calculation: Adjusts table body height based on available viewport space
 * - Skeleton loading: Shows skeleton rows during loading with customizable renderers
 * - Virtual scrolling: Optional virtual scrolling for better performance with large datasets
 * - Row selection: Optional internal row selection management with visual highlighting
 * - Auto-scroll to selected: Automatically scrolls to selected row (works with virtual scrolling)
 * - Responsive: Adapts to container size changes using ResizeObserver
 * - Flexible row keys: Supports function-based, string-based, or automatic row key detection
 * 
 * @template T - The type of data records in the table
 */
export function AutoHeightTable<T extends object>(
    {
      columns,
      data,
      loading = false,
      rowKey,
      enableVirtual = true,
      subtractPx = 0,
      withCard = true,
      enableRowSelection = false,
      onRow: onRowCallback,
      beforeComponent,
      ...props
    }: AutoHeightTableProps<T>
) {
  const wrapRef = useRef<HTMLDivElement>(null);

  // Internal row selection state (only used when enableRowSelection=true)
  // When enabled, the component manages selection internally without requiring external state
  const [internalSelectedRowKey, setInternalSelectedRowKey] = useState<React.Key | null>(null);

  // Calculate selectedIndex from selectedRowKey and data
  // This is used for visual highlighting and scroll-to-selected functionality
  const selectedIndex = useMemo(() => {
    if (!enableRowSelection || !internalSelectedRowKey || !data || data.length === 0) return -1;
    
    // Filter out skeleton rows for index calculation
    const realData = data.filter((record) => !isSkeletonRow(record));
    
    // Find the index in real data
    const index = realData.findIndex((record) => {
      const key = typeof rowKey === "function" 
        ? rowKey(record)
        : typeof rowKey === "string"
        ? (record as unknown as Record<string, unknown>)[rowKey]
        : (record as unknown as { key?: React.Key }).key;
      return key === internalSelectedRowKey;
    });
    
    return index >= 0 ? index : -1;
  }, [enableRowSelection, internalSelectedRowKey, data, rowKey]);

  const {height} = useAutoSize(wrapRef);
  // Calculate available height for table body (keep >= 0)
  const y = Math.max(0, height - subtractPx);

  // Apply skeleton rendering to columns when loading
  const columnsApplied = React.useMemo(
      () => applyColumnSkeletons(columns, loading),
      [columns, loading]
  );
  // Calculate the number of skeleton rows based on available height
  // Assumption: ~60px per row (header + padding)
  const skeletonCount = Math.max(3, Math.ceil(y / 60));
  const dataToShow = React.useMemo(
      () => buildDisplayData(data, loading, skeletonCount),
      [data, loading, skeletonCount]
  );

  // Scroll-to-selected effect (works with both normal and virtual scrolling)
  useLayoutEffect(() => {
    const container = wrapRef.current;
    if (!container) return;
    const selectedKey = enableRowSelection ? internalSelectedRowKey : null;
    handleScrollTo(container, selectedKey, selectedIndex, enableVirtual);
  }, [enableRowSelection, internalSelectedRowKey, selectedIndex, enableVirtual, y]);

  // Merge user-provided row class names with selection styling
  const userRowClassName = props.rowClassName;

  const mergedRowClassName: TableProps<T>["rowClassName"] = (
      record: T,
      index: number,
      indent: number
  ) => {
    // Add selection class if this row is selected
    const sel = index === selectedIndex ? "ant-table-row-selected" : "";
    // Get user-provided class name (function or string)
    const user =
        typeof userRowClassName === "function"
            ? userRowClassName(record, index, indent) // Pass all args to user function
            : (userRowClassName ?? "");               // Use string or empty string

    // Combine user classes and selection class, filtering out empty strings
    return [user, sel].filter(Boolean).join(" ");
  };


  /**
   * Safely extracts the row key from a record.
   * Handles multiple scenarios: skeleton rows, function-based keys, string-based keys,
   * and fallbacks. Always returns a valid React.Key.
   */
  const safeRowKey = (record: T): React.Key => {
    // 1) Skeleton rows: always use the predefined record.key
    if (isSkeletonRow(record)) return record.key;

    // 2) User provided a rowKey function: call it with the record
    if (typeof rowKey === "function") {
      return rowKey(record);
    }

    // 3) User provided rowKey as a field name (string): extract from record
    if (typeof rowKey === "string") {
      const k = (record as unknown as Record<string, unknown>)[rowKey];
      if (k !== null && k !== undefined) return k as React.Key;
    }

    // 4) Fallback: accept existing record.key if the dataset provides it
    const k = (record as unknown as { key?: React.Key }).key;
    if (k !== null && k !== undefined) return k;

    // Last resort: hash-based fallback
    // ⚠️ Better: fix data source so 'id' or 'key' exists.
    return JSON.stringify(record); // Only temporary in DEV, can be large
  };

  // Wrap onRow callback to handle selection when enableRowSelection is true
  // When enabled, clicking a row will first select it, then execute the original onClick handler
  const wrappedOnRow: TableProps<T>["onRow"] = useMemo(
      () => wrapOnRowWithSelection(
          onRowCallback,
          enableRowSelection,
          safeRowKey,
          setInternalSelectedRowKey
      ),
      [onRowCallback, enableRowSelection, safeRowKey]
  );

  const table = (
      <div className={"grid overflow-hidden gap-y-1"}>
        {beforeComponent}
        <div ref={wrapRef} className="overflow-hidden h-full">
          <Table<T>
              {...props}
              rowKey={safeRowKey}
              columns={columnsApplied}
              dataSource={dataToShow}
              // loading={spinProps}
              pagination={false}
              locale={{
                emptyText: loading ? (
                    <Skeleton active={true}/>
                ) : (
                    <Empty
                        image={<image className={"h-0 w-0"}/>}
                        description={
                          <Typography.Title level={5}>Keine Daten</Typography.Title>
                        }
                    />
                ),
              }}
              scroll={{y, x: 1, scrollToFirstRowOnChange: false}}
              virtual={enableVirtual}
              rowClassName={mergedRowClassName}
              onRow={wrappedOnRow}
          />
        </div>
      </div>
  );

  return (
      <div className={"h-full grid overflow-hidden"}>
        {/* Grid layout passes height down to children */}
        {withCard ? (
            <Card
                className={"h-full overflow-hidden"}
                classNames={{
                  body: "h-full overflow-hidden",
                }}
            >
              <div className={"grid h-full overflow-hidden"}>{table}</div>
            </Card>
        ) : (
            table
        )}
      </div>
  );
}
